import React from 'react';
import { Files } from 'lucide-react';

/**
 * Wl* page primitives — the shared building blocks of every workspace page
 * (page header, section card, stat tile, avatar, progress bar, placeholder).
 *
 * These are the de facto design system for app pages; styles live in
 * styles/app-shell.css (`.wl-*` classes). Extracted from AppShell.tsx so pages
 * can use them without depending on the shell itself; AppShell re-exports them
 * for backwards compatibility with existing imports.
 */

function mkInitials(name: string) {
  return name
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const PALETTE = ['#1B2A4E', '#3FB39A', '#5B7CC3', '#E89B4A', '#7C5BC4', '#2E9982', '#3A4A6E'];

function avatarBg(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

export function WlAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={`wl-avatar ${size}`} style={{ background: avatarBg(name), color: '#fff' }}>
      {mkInitials(name)}
    </span>
  );
}

export function WlPage({
  title,
  subtitle,
  actions,
  children,
}: {
  title?: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="wl-page wl-fade-in">
      {(title || actions) && (
        <div className="wl-page-head">
          <div>
            {title && <h1 className="t">{title}</h1>}
            {subtitle && <div className="sub">{subtitle}</div>}
          </div>
          {actions && <div className="actions">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function WlStat({
  label,
  value,
  delta,
  icon,
  trend = 'up',
}: {
  label: string;
  value: string;
  delta?: string;
  icon?: React.ReactNode;
  /** 'none' renders the caption without an arrow (descriptive, not a change). */
  trend?: 'up' | 'down' | 'none';
}) {
  return (
    <div className="wl-stat">
      <div className="l">
        <div className="lbl">{label}</div>
        {icon && <div className="ic">{icon}</div>}
      </div>
      <div className="num">{value}</div>
      {delta && (
        <div
          className={`delta ${trend === 'down' ? 'down' : ''}`}
          style={trend === 'none' ? { color: 'var(--ink-4)' } : undefined}
        >
          {trend === 'down' ? (
            <span style={{ fontSize: 12 }}>↓</span>
          ) : trend === 'up' ? (
            <span style={{ fontSize: 12 }}>↑</span>
          ) : null}
          {delta}
        </div>
      )}
    </div>
  );
}

export function WlSection({
  title,
  action,
  children,
  style,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="wl-section" style={style}>
      {(title || action) && (
        <div className="wl-section-head">
          <h2>{title}</h2>
          {action}
        </div>
      )}
      <div className="wl-section-body">{children}</div>
    </div>
  );
}

/** WAI-ARIA APG tabs: roving tabindex, Arrow/Home/End keys, panel wiring.
 *  Pair with a container carrying `id={`${idBase}-panel`}` role="tabpanel"
 *  aria-labelledby={`${idBase}-tab-${active}`}. */
export function WlTabList<K extends string>({
  label,
  idBase,
  tabs,
  active,
  onChange,
  orientation = 'horizontal',
  style,
}: {
  label: string;
  idBase: string;
  tabs: Array<{ key: K; label: string }>;
  active: K;
  onChange: (key: K) => void;
  orientation?: 'horizontal' | 'vertical';
  style?: React.CSSProperties;
}) {
  const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
  const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (![prevKey, nextKey, 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const idx = tabs.findIndex((t) => t.key === active);
    const nextIdx =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? tabs.length - 1
          : e.key === prevKey
            ? (idx - 1 + tabs.length) % tabs.length
            : (idx + 1) % tabs.length;
    const next = tabs[nextIdx].key;
    onChange(next);
    document.getElementById(`${idBase}-tab-${next}`)?.focus();
  };
  return (
    <div
      className={`wl-tabs ${orientation === 'vertical' ? 'vertical' : ''}`}
      role="tablist"
      aria-label={label}
      aria-orientation={orientation}
      onKeyDown={onKeyDown}
      style={style}
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          id={`${idBase}-tab-${t.key}`}
          role="tab"
          aria-selected={active === t.key}
          aria-controls={`${idBase}-panel`}
          tabIndex={active === t.key ? 0 : -1}
          className={`wl-tab ${active === t.key ? 'active' : ''}`}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function WlProgressBar({ value = 0, teal }: { value?: number; teal?: boolean }) {
  return (
    <div className={`wl-bar ${teal ? 'teal' : ''}`}>
      <i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

export function WlPlaceholder({
  title,
  subtitle,
  icon,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
}) {
  return (
    <WlPage title={title} subtitle={subtitle}>
      <div className="wl-empty">
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 14,
            background: 'var(--bg-2)',
            color: 'var(--ink-4)',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 16px',
          }}
        >
          {icon ?? <Files size={26} />}
        </div>
        <h3 style={{ marginBottom: 6 }}>{title} workspace</h3>
        <div style={{ fontSize: 13.5, color: 'var(--ink-4)', maxWidth: 380, margin: '0 auto' }}>
          This page is part of the Welynk workspace. Full functionality is coming soon.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 18 }}>
          <button className="wl-btn wl-btn-secondary">Take a tour</button>
          <button className="wl-btn wl-btn-primary">
            <span style={{ fontSize: 16, lineHeight: 1, marginTop: -1 }}>+</span> Add new
          </button>
        </div>
      </div>
    </WlPage>
  );
}
